import type { ReactNode } from 'react'
import { useFeature } from '../index.js'
import type { SessionRole } from '../../core/index.js'
import { Link, Skeleton } from '../../core/index.js'

interface FeatureGateProps {
  /** The add-on's catalog key. */
  feature: string
  /** Its display name, for the fallback copy. */
  name: string
  facilityId: string | null
  /** Who is looking, for the fallback's advice. */
  role: SessionRole | null
  /** Where this facility's Add-ons tab is, for an operator who can switch it on. */
  addOnsHref?: string
  /** For a contribution inside a roster: nothing at all while unlicensed, one line while switched off. */
  compact?: boolean
  children: ReactNode
}

/**
 * Renders an add-on's screen only while the add-on is on at the facility;
 * otherwise says why, by role (docs/plans/feature-licensing-plan.md
 * section 4). An operator is pointed at the switch; a facility
 * administrator at their operator; an unlicensed client at SeeWhoCares.
 */
export function FeatureGate({ feature, name, facilityId, addOnsHref, compact = false, children }: FeatureGateProps) {
  const standing = useFeature(facilityId, feature)
  if (standing.isLoading) {
    return compact ? null : <Skeleton className="p-6" />
  }
  if (standing.enabled) {
    return <>{children}</>
  }
  if (compact) {
    if (!standing.licensed) {
      return null
    }
    return (
      <p className="text-[11px] text-ink-mid">
        {name} is switched off for this facility.{' '}
        {addOnsHref ? (
          <Link to={addOnsHref} className="font-bold text-marine hover:text-marine-dk">
            Switch it on under Add-ons
          </Link>
        ) : (
          'Switch it on under Configuration › Add-ons.'
        )}
      </p>
    )
  }
  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="bg-white border border-marine/15 rounded p-5">
        <div className="text-sm font-black text-ink mb-1">{name} is {standing.licensed ? 'switched off for this facility' : 'not part of your agreement'}</div>
        <p className="text-xs text-ink-mid">
          {!standing.licensed
            ? `${name} is an add-on. Ask autonoME to add it to your agreement.`
            : 'Your agreement includes it. Switch it on for this facility under Configuration › Add-ons.'}
        </p>
        {standing.licensed && addOnsHref && (
          <Link to={addOnsHref} className="inline-block mt-3 text-xs font-bold text-marine hover:text-marine-dk">
            Open Add-ons
          </Link>
        )}
      </div>
    </div>
  )
}
